///
/// EarthBrowser
///
/// Created by Matt Giger on 9/11/2026
/// Copyright (c) 2026 Geodata Labs
///

import { labelOccupancy, type LabelRect } from "@/earthbrowser/design-system/LabelOccupancy"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { MapLabelsOverlay, type MapLabelData, type MapLabelKind, type MapLabelSpec } from "@/earthbrowser/entities/VectorTiles/MapLabelsOverlay"
import { GeoPolylineGeometry, type GeoLinePoint } from "@/earthbrowser/entities/VectorTiles/GeoPolylineGeometry"
import { GeoPolylineMaterial, GeoPolylineRenderer, type GeoPolylineStyle } from "@/earthbrowser/entities/VectorTiles/GeoPolylineRenderer"
import { LineDensityGrid } from "@/earthbrowser/entities/VectorTiles/LineDensityGrid"
import { MVTDecoder } from "@/earthbrowser/vector-tiles/MVTDecoder"
import { VectorTileId } from "@/earthbrowser/vector-tiles/VectorTileId"
import { vectorTileManager } from "@/earthbrowser/vector-tiles/VectorTileManager"
import { VectorTileGeometryType, type DecodedVectorTile } from "@/earthbrowser/vector-tiles/types"
import { getTileToken } from "@/services/tiles/TileTokenService"
import { type Perspective } from "@stovokor/camera/Perspective"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { trig } from "@stovokor/math/math"
import { sphere } from "@stovokor/math/sphere"
import { dot, normal, type vec3 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"

/** Style applied to line features from one MVT source layer */
export interface VectorLineStyle extends GeoPolylineStyle {
	id: string
	/** Name of the layer inside the MVT tile (tippecanoe -l value) */
	sourceLayer: string
	/** Only render this style at these tile levels (inclusive) */
	minLevel?: number
	maxLevel?: number
	/**
	 * Cross-fade with camera altitude (km): fully transparent at or above
	 * [0], fully opaque at or below [1]. Use with minLevel so tiles at the
	 * level where the fade begins carry the style
	 */
	fadeAltitudeKm?: [number, number]
	/** Optional per-feature property filter */
	filter?: (properties: Record<string, unknown>) => boolean
}

/** Labels rendered from point features in one MVT source layer */
export interface VectorLabelOptions {
	/** Name of the point layer inside the MVT tile, e.g. "division_labels" */
	sourceLayer: string
	/** Maps point feature properties to a label spec (undefined = skip) */
	extract: (properties: Record<string, unknown>) => MapLabelSpec | undefined
	/** Cap on simultaneously placed labels */
	maxLabels?: number
	/**
	 * Labels of a kind fade with the line density of the named style (which
	 * must set densityFade), so a division's name goes with its boundary
	 */
	densityFadeByKind?: Partial<Record<MapLabelKind, string>>
}

export interface VectorTileLayerOptions {
	/** Source id used as the tile cache key */
	source: string
	/** Tile URL template, e.g. https://tiles.earthbrowser.com/borders/{z}/{x}/{y}.pbf */
	urlTemplate: string
	minLevel: number
	maxLevel: number
	styles: VectorLineStyle[]
	/** Attach a tile-server JWT to requests (default true) */
	authenticated?: boolean
	/** Render name labels from a point layer in the same tiles */
	labels?: VectorLabelOptions
}

// One draw call per style bucket per tile
interface TileBucket {
	style: VectorLineStyle
	geometry: GeoPolylineGeometry
	material: GeoPolylineMaterial
	renderer: GeoPolylineRenderer
}

// One label point decoded from the tile's label source layer
interface VectorLabelPoint extends MapLabelSpec {
	cartesian: vec3
	surfaceNormal: vec3
	/** Line density (km/km²) at the point and the spacing fade governing it */
	density?: number
	densityFade?: [number, number]
}

interface LoadedTile {
	id: VectorTileId
	buckets: TileBucket[]
	labels: VectorLabelPoint[]
	lastUsed: number
	/** Built after the async base-elevation layer loaded (else rebuilt later) */
	hasElevation: boolean
}

// Bounding box (radians) of the points extending a label, with their count
interface LabelExtent {
	minLon: number
	maxLon: number
	minLat: number
	maxLat: number
	count: number
}

const cUpdateInterval = 0.25			// seconds between visible-set recomputes
const cRaySamples = 8					// viewport sampling grid resolution
const cMaxInFlight = 12					// concurrent tile fetches
const cMaxCachedTiles = 160				// LRU cap on built tiles
const cPruneAgeSecs = 20				// destroy tiles unused for this long
const cFailedRetrySecs = 300			// wait before retrying a failed/empty tile
// Tile level heuristic: level = log2(cLevelScale / cameraAltitudeKm)
const cLevelScale = 40000

// Label defaults
const cMaxLabels = 40					// simultaneously placed labels
const cLabelDepthLiftFrac = 0.02		// lift depth sample toward camera (vs terrain z-fight)
const cLabelScreenMargin = 80			// px beyond the viewport still considered placeable
const cLabelPaddingPx = 8				// min gap between label rects
// A label whose extent (diagonal km) exceeds the viewport's ground footprint
// by these ratios starts fading / is gone: once you are inside a country
// its name no longer tells you anything. A narrow band so a static view
// rarely lands on a half-faded name
const cExtentFadeStart = 1.3
const cExtentFadeEnd = 1.7
const cExtentMinPoints = 2				// fewer extending points: extent unknown, no fade

// Terrain tiles displace vertices by elevation * this (matches earthtile.glsl),
// so CPU-side anchors must scale the same way to sit on the rendered surface
const cElevationMultiplier = 1.2

/**
 * Renders vector tile line data (borders, roads, rail) draped on the globe.
 *
 * Mirrors the WebMercatorOverlayTileSet lifecycle: visible tiles are found by
 * sampling view rays against the globe, fetched/cached through the
 * VectorTileManager (SQLite-backed), decoded from MVT, and built into batched
 * GeoPolylineGeometry buckets, one per style per tile.
 */
export class VectorTileLayerNode extends NodeBase {
	private context: StovokorContext
	private options: VectorTileLayerOptions
	private globe = new sphere(wgs.a)

	private tiles = new Map<string, LoadedTile>()
	private inFlight = new Set<string>()
	private failed = new Map<string, number>()

	/** Layers-panel toggle for the name labels, independent of the lines */
	public labelsVisible = true

	private targetLevel = 0
	private displayLevel = -1
	private displayKeys: string[] = []
	private nextUpdate = 0
	private destroyed = false

	private labelOverlay?: MapLabelsOverlay
	private labelCandidates: VectorLabelPoint[] = []
	private labelCacheKey = ""
	// Geographic extent of a label (e.g. a country) sketched by the points
	// that extend it (its regions), accumulated over every tile ever loaded
	// and never pruned: a few hundred entries at most
	private labelExtents = new Map<string, LabelExtent>()

	constructor(context: StovokorContext, options: VectorTileLayerOptions) {
		super()
		this.context = context
		this.options = options
	}

	override render(state: RenderState): RenderActionSet[] {
		if (!this.visible) {
			this.labelOverlay?.updateLabels([])
			labelOccupancy.clear(`vector-labels:${this.options.source}`)
			return []
		}

		const now = performance.now() * 0.001
		if (now >= this.nextUpdate) {
			this.nextUpdate = now + cUpdateInterval
			this.updateVisibleTiles(state, now)
		}

		const altitude = state.camera.position.length - wgs.a
		const actions: RenderActionSet[] = []
		for (const key of this.displayKeys) {
			const tile = this.tiles.get(key)
			if (!tile) {
				continue
			}
			tile.lastUsed = now

			for (const bucket of tile.buckets) {
				const style = bucket.style
				if (this.targetLevel < (style.minLevel ?? 0) || this.targetLevel > (style.maxLevel ?? Infinity)) {
					continue
				}
				const opacity = VectorTileLayerNode.styleOpacity(style, altitude)
				if (opacity <= 0) {
					continue
				}
				bucket.material.opacity = opacity

				// Frustum cull each bucket by its world-space bounds
				const xsphere = bucket.geometry.boundingSphere.transform(state.modelView)
				if (state.frustum.intersects(xsphere) !== "outside") {
					actions.push(bucket.renderer.render(state, state.viewDepthOf(bucket.geometry.boundingSphere.center)))
				}
			}
		}

		this.updateLabels(state)

		return actions.concat(this.renderChildren(state))
	}

	/** Altitude cross-fade: 1 when the style has none, else eased 0..1 across its band */
	private static styleOpacity(style: VectorLineStyle, altitudeKm: number): number {
		const fade = style.fadeAltitudeKm
		if (!fade) {
			return 1
		}
		const [start, end] = fade
		if (start <= end) {
			return 1
		}
		const t = Math.min(Math.max((start - altitudeKm) / (start - end), 0), 1)
		return t * t * (3 - 2 * t)
	}

	/**
	 * Project, cull, and declutter division name labels into the overlay.
	 * The overlay draws in the depth-preserving label pass, so labels on the
	 * far side of the globe are occluded via their worldDepth.
	 */
	private updateLabels(state: RenderState): void {
		const options = this.options.labels
		if (!options) {
			return
		}
		const occupancyOwner = `vector-labels:${this.options.source}`
		if (!this.labelsVisible) {
			this.labelOverlay?.updateLabels([])
			labelOccupancy.clear(occupancyOwner)
			return
		}

		if (!this.labelOverlay) {
			this.labelOverlay = new MapLabelsOverlay(this.context)
			this.labelOverlay.depth = 0
			const ebcontext = this.context as EarthBrowserContext
			ebcontext.selectionCoordinator.addLabelNode(this.labelOverlay)
		}

		const camera = state.camera
		const cameraPos = camera.position
		const camDist = cameraPos.length
		const altitude = camDist - wgs.a
		const cameraNormal = normal(cameraPos)
		// Points past the horizon circle face away from the camera
		const horizonCos = Math.min(wgs.a / camDist, 1)
		const maxLabels = options.maxLabels ?? cMaxLabels

		// Ground footprint of the viewport (km) for the extent fade: the
		// longer side at the camera's altitude, ignoring tilt
		const fovDeg = (camera.projection as Perspective).fov ?? 45
		const halfTan = Math.tan(fovDeg * 0.5 * trig.rad)
		const viewSpanKm = altitude * 2 * halfTan * Math.max(state.width / Math.max(state.height, 1), 1)
		// Pixels per km at unit depth (same term as the line shader's pxPerKm)
		const pxPerKmAtUnitDepth = state.height * 0.5 / halfTan

		// Candidates: on-screen, camera-facing label points from displayed tiles,
		// countries first then by population so declutter keeps the important ones
		const candidates: Array<{ point: VectorLabelPoint, screenX: number, screenY: number, depth: number, alpha: number }> = []
		for (const point of this.gatherLabelPoints()) {
			if (altitude > point.maxAltitudeKm) {
				continue
			}
			if (dot(point.surfaceNormal, cameraNormal) <= horizonCos + 0.01) {
				continue
			}
			const alpha = this.extentAlpha(point.id, viewSpanKm)
			if (point.density !== undefined && point.densityFade) {
				// Same spacing measure as the lines, but a label is either
				// placed or not (the overlay animates the change): it appears
				// once its lines are at least half faded in, so half-ghost
				// names never sit over invisible boundaries
				const eyeDist = cameraPos.subtract(point.cartesian).length
				const spacingPx = pxPerKmAtUnitDepth / Math.max(eyeDist, 1e-3) / Math.max(point.density, 1e-6)
				if (spacingPx < (point.densityFade[0] + point.densityFade[1]) * 0.5) {
					continue
				}
			}
			if (alpha <= 0.05) {
				continue
			}

			const screen = camera.worldToScreen(point.cartesian)
			if (!Number.isFinite(screen.x) || !Number.isFinite(screen.y)) {
				continue
			}
			if (screen.x < -cLabelScreenMargin || screen.x > state.width + cLabelScreenMargin
				|| screen.y < -cLabelScreenMargin || screen.y > state.height + cLabelScreenMargin) {
				continue
			}

			// Lift the depth sample toward the camera so displaced terrain
			// can't z-fight the label; the globe still occludes it
			const toCamera = cameraPos.subtract(point.cartesian)
			const lifted = point.cartesian.add(normal(toCamera).multiply(toCamera.length * cLabelDepthLiftFrac))
			candidates.push({ point, screenX: screen.x, screenY: screen.y, depth: camera.worldToScreenDepth(lifted), alpha })
		}

		candidates.sort((a, b) => b.point.priority - a.point.priority)

		// Greedy AABB declutter using the extractor's estimated label extents,
		// seeded with the rects other label systems (city/weather labels,
		// another vector layer's labels) are drawing so these yield to them
		const placed: LabelRect[] = labelOccupancy.others(occupancyOwner)
		const own: LabelRect[] = []
		const labels: MapLabelData[] = []
		for (const candidate of candidates) {
			if (labels.length >= maxLabels) {
				break
			}
			const width = candidate.point.widthPx
			const height = candidate.point.heightPx
			const rect = {
				left: candidate.screenX - width / 2 - cLabelPaddingPx,
				top: candidate.screenY - height / 2 - cLabelPaddingPx,
				right: candidate.screenX + width / 2 + cLabelPaddingPx,
				bottom: candidate.screenY + height / 2 + cLabelPaddingPx
			}
			if (placed.some(other => rect.left < other.right && rect.right > other.left
				&& rect.top < other.bottom && rect.bottom > other.top)) {
				continue
			}
			placed.push(rect)
			own.push(rect)
			labels.push({
				id: candidate.point.id,
				text: candidate.point.text,
				kind: candidate.point.kind,
				network: candidate.point.network,
				symbol: candidate.point.symbol,
				screenX: candidate.screenX,
				screenY: candidate.screenY,
				worldDepth: candidate.depth,
				alpha: candidate.alpha
			})
		}

		labelOccupancy.publish(occupancyOwner, own)
		this.labelOverlay.updateLabels(labels)
	}

	/**
	 * Opacity of a label from how its known extent compares to the viewport:
	 * 1 while the whole of it fits (or its extent is unknown), fading out as
	 * the camera moves inside it
	 */
	private extentAlpha(labelId: string, viewSpanKm: number): number {
		const extent = this.labelExtents.get(labelId)
		if (!extent || extent.count < cExtentMinPoints || viewSpanKm <= 0) {
			return 1
		}
		const spanKm = GeoPolylineGeometry.distanceKm(
			{ lon: extent.minLon, lat: extent.minLat },
			{ lon: extent.maxLon, lat: extent.maxLat }
		)
		const t = Math.min(Math.max((spanKm / viewSpanKm - cExtentFadeStart) / (cExtentFadeEnd - cExtentFadeStart), 0), 1)
		return 1 - t * t * (3 - 2 * t)
	}

	/** Union of label points across displayed tiles, deduped by division id */
	private gatherLabelPoints(): VectorLabelPoint[] {
		let loadedCount = 0
		for (const key of this.displayKeys) {
			if (this.tiles.has(key)) {
				loadedCount++
			}
		}
		const cacheKey = `${this.displayKeys.join(",")}|${loadedCount}`
		if (cacheKey === this.labelCacheKey) {
			return this.labelCandidates
		}

		const unique = new Map<string, VectorLabelPoint>()
		for (const key of this.displayKeys) {
			const tile = this.tiles.get(key)
			if (tile) {
				for (const point of tile.labels) {
					unique.set(point.id, point)
				}
			}
		}

		this.labelCacheKey = cacheKey
		this.labelCandidates = Array.from(unique.values())
		return this.labelCandidates
	}

	/**
	 * Recompute the set of tiles covering the viewport, kick off loads for
	 * missing ones, and advance displayLevel once the target level is complete
	 */
	private updateVisibleTiles(state: RenderState, now: number): void {
		const camera = state.camera
		const altitude = camera.position.length - wgs.a
		if (altitude <= 0) {
			return
		}

		// The base-elevation layer loads async at startup: rebuild any tiles
		// built before it was ready so their lines/labels drape onto terrain
		const ebcontext = this.context as EarthBrowserContext
		if (ebcontext.baseElevation.loaded) {
			for (const [key, tile] of this.tiles) {
				if (!tile.hasElevation) {
					this.destroyTile(tile)
					this.tiles.delete(key)
				}
			}
		}

		// Layers with a nonzero minLevel (roads) hide entirely when the camera
		// is too high, rather than fanning out over the whole hemisphere
		const rawLevel = Math.round(Math.log2(cLevelScale / altitude))
		if (rawLevel < this.options.minLevel) {
			this.displayKeys = []
			this.displayLevel = -1
			return
		}

		this.targetLevel = Math.min(Math.max(rawLevel, this.options.minLevel), this.options.maxLevel)

		const required = this.requiredTiles(state, this.targetLevel)

		// Queue missing tiles by need
		let inFlightCount = this.inFlight.size
		let allLoaded = true
		for (const id of required.values()) {
			if (this.tiles.has(id.key)) {
				continue
			}
			allLoaded = false

			const failedAt = this.failed.get(id.key)
			if (failedAt !== undefined && now - failedAt < cFailedRetrySecs) {
				continue
			}
			if (!this.inFlight.has(id.key) && inFlightCount < cMaxInFlight) {
				inFlightCount++
				void this.loadTile(id)
			}
		}

		if (allLoaded || this.displayLevel < 0) {
			this.displayLevel = this.targetLevel
		}

		// Display whichever level is complete; recompute its coverage if it
		// differs from the target so stale tiles bridge the transition
		const displaySet = this.displayLevel === this.targetLevel
			? required
			: this.requiredTiles(state, this.displayLevel)
		this.displayKeys = Array.from(displaySet.values()).map(id => id.key)

		this.pruneTiles(now)
	}

	/** Find tiles covering the viewport by sampling view rays against the globe */
	private requiredTiles(state: RenderState, level: number): Map<string, VectorTileId> {
		const camera = state.camera
		const tiles = new Map<string, VectorTileId>()

		const stepX = state.width / cRaySamples
		const stepY = state.height / cRaySamples
		for (let x = stepX / 2; x < state.width; x += stepX) {
			for (let y = stepY / 2; y < state.height; y += stepY) {
				const ray = camera.rayFrom(x, y)
				const dist = this.globe.intersect(ray)
				if (dist > 0) {
					const geo = new geographic(ray.pointAt(dist))
					const id = VectorTileId.fromLngLat(geo.longitude * trig.deg, geo.latitude * trig.deg, level)
					tiles.set(id.key, id)
				}
			}
		}

		return tiles
	}

	private async loadTile(id: VectorTileId): Promise<void> {
		this.inFlight.add(id.key)
		try {
			const headers: Record<string, string> = {}
			if (this.options.authenticated !== false) {
				const token = await getTileToken()
				if (token) {
					headers.Authorization = `Bearer ${token}`
				}
			}

			const result = await vectorTileManager.fetchTile(id, {
				source: this.options.source,
				urlTemplate: this.options.urlTemplate,
				headers,
				maxRetries: 1
			})

			if (this.destroyed) {
				return
			}

			if (result.success && result.data && result.data.byteLength > 0) {
				const decoded = MVTDecoder.decode(result.data, id)
				this.tiles.set(id.key, this.buildTile(id, decoded))
			} else {
				// Includes empty (204) ocean tiles: retry only occasionally
				this.failed.set(id.key, performance.now() * 0.001)
			}
		} catch {
			this.failed.set(id.key, performance.now() * 0.001)
		} finally {
			this.inFlight.delete(id.key)
		}
	}

	/** Terrain height (km) above the ellipsoid at a position (radians) */
	private terrainElevationKm(lon: number, lat: number): number {
		const ebcontext = this.context as EarthBrowserContext
		const meters = ebcontext.baseElevation.getElevation(new geographic(lon, lat))
		return meters > 0 ? meters / 1000 : 0
	}

	/** Decode MVT features into one batched geometry per style */
	private buildTile(id: VectorTileId, decoded: DecodedVectorTile): LoadedTile {
		const buckets: TileBucket[] = []
		// Density grids by style id, for labels that fade with their lines
		const densityGrids = new Map<string, LineDensityGrid>()

		// Subdivide segments to roughly 1/32 of the tile width so lines follow
		// the globe's curvature (matters at low zoom)
		const n = 1 << id.z
		const maxSegmentRadians = trig.two_pi / n / 32
		const elevationSampler = (lon: number, lat: number) => this.terrainElevationKm(lon, lat)

		for (const style of this.options.styles) {
			const layer = decoded.layers.get(style.sourceLayer)
			if (!layer) {
				continue
			}

			const lines: GeoLinePoint[][] = []
			for (const feature of layer.features) {
				if (feature.type !== VectorTileGeometryType.LINESTRING && feature.type !== VectorTileGeometryType.POLYGON) {
					continue
				}
				if (style.filter && !style.filter(feature.properties)) {
					continue
				}

				for (const part of feature.geometry) {
					if (part.length >= 2) {
						lines.push(part.map(pt => this.tileLocalToGeo(id, pt.x, pt.y, feature.extent)))
					}
				}
			}

			if (lines.length > 0) {
				// Density-faded styles bin their line length over the tile so
				// each vertex knows how crowded its neighborhood is
				let densitySampler: ((lon: number, lat: number) => number) | undefined
				if (style.densityFade) {
					const topLeft = this.tileLocalToGeo(id, 0, 0, 1)
					const bottomRight = this.tileLocalToGeo(id, 1, 1, 1)
					const grid = new LineDensityGrid({
						minLon: topLeft.lon,
						maxLon: bottomRight.lon,
						minLat: bottomRight.lat,
						maxLat: topLeft.lat
					})
					grid.accumulate(lines)
					grid.finalize()
					densitySampler = (lon, lat) => grid.sample(lon, lat)
					densityGrids.set(style.id, grid)
				}

				const geometry = new GeoPolylineGeometry(this.context, { lines, maxSegmentRadians, elevationSampler, densitySampler })
				const material = new GeoPolylineMaterial(style)
				const renderer = new GeoPolylineRenderer(this.context, geometry, material)
				buckets.push({ style, geometry, material, renderer })
			}
		}

		const ebcontext = this.context as EarthBrowserContext
		return {
			id,
			buckets,
			labels: this.extractLabels(id, decoded, densityGrids),
			lastUsed: performance.now() * 0.001,
			hasElevation: ebcontext.baseElevation.loaded
		}
	}

	/** Decode point features from the label source layer into label points */
	private extractLabels(id: VectorTileId, decoded: DecodedVectorTile, densityGrids: Map<string, LineDensityGrid>): VectorLabelPoint[] {
		const options = this.options.labels
		if (!options) {
			return []
		}
		const layer = decoded.layers.get(options.sourceLayer)
		if (!layer) {
			return []
		}

		// Which style's density governs each label kind, when that style
		// built a grid for this tile
		const densityByKind = new Map<MapLabelKind, { grid: LineDensityGrid, fade: [number, number] }>()
		for (const [kind, styleId] of Object.entries(options.densityFadeByKind ?? {})) {
			const grid = densityGrids.get(styleId)
			const fade = this.options.styles.find(style => style.id === styleId)?.densityFade
			if (grid && fade) {
				densityByKind.set(kind as MapLabelKind, { grid, fade })
			}
		}

		const labels: VectorLabelPoint[] = []
		for (const feature of layer.features) {
			if (feature.type !== VectorTileGeometryType.POINT) {
				continue
			}
			const spec = options.extract(feature.properties)
			const point = feature.geometry[0]?.[0]
			if (!spec || !point) {
				continue
			}

			const geo = this.tileLocalToGeo(id, point.x, point.y, feature.extent)
			if (spec.extendsLabel) {
				this.extendLabel(spec.extendsLabel, geo.lon, geo.lat)
			}
			// Anchor on the displaced terrain surface (matching the elevation
			// multiplier the tile shader applies) so tilted views don't sink
			// the label below the ground and fail its depth test
			const elev = this.terrainElevationKm(geo.lon, geo.lat) * cElevationMultiplier
			const cartesian = new geographic(geo.lon, geo.lat, elev).cartesian
			// Multi-placement labels (road shields) key on the rounded position
			// (radians, ~6 km buckets) so the same route places repeatedly
			// while duplicate points from overlapping tiles still collapse
			const labelId = spec.dedupeByPosition
				? `${spec.id}@${geo.lon.toFixed(3)},${geo.lat.toFixed(3)}`
				: spec.id
			const density = densityByKind.get(spec.kind)
			labels.push({
				...spec,
				id: labelId,
				cartesian,
				surfaceNormal: normal(cartesian),
				density: density?.grid.sample(geo.lon, geo.lat),
				densityFade: density?.fade
			})
		}

		return labels
	}

	/** Grow a label's extent to include a point (radians) */
	private extendLabel(labelId: string, lon: number, lat: number): void {
		const extent = this.labelExtents.get(labelId)
		if (!extent) {
			this.labelExtents.set(labelId, { minLon: lon, maxLon: lon, minLat: lat, maxLat: lat, count: 1 })
			return
		}
		extent.minLon = Math.min(extent.minLon, lon)
		extent.maxLon = Math.max(extent.maxLon, lon)
		extent.minLat = Math.min(extent.minLat, lat)
		extent.maxLat = Math.max(extent.maxLat, lat)
		extent.count++
	}

	/** Convert MVT tile-local coordinates to geographic radians (inverse Web Mercator) */
	private tileLocalToGeo(id: VectorTileId, px: number, py: number, extent: number): GeoLinePoint {
		const n = 1 << id.z
		const u = (id.x + px / extent) / n
		const v = (id.y + py / extent) / n
		return {
			lon: u * trig.two_pi - trig.pi,
			lat: 2 * Math.atan(Math.exp((0.5 - v) * trig.two_pi)) - trig.half_pi
		}
	}

	/** Destroy tiles that haven't been rendered recently, keeping the cache bounded */
	private pruneTiles(now: number): void {
		const displaySet = new Set(this.displayKeys)

		for (const [key, tile] of this.tiles) {
			if (!displaySet.has(key) && now - tile.lastUsed > cPruneAgeSecs) {
				this.destroyTile(tile)
				this.tiles.delete(key)
			}
		}

		// LRU cap as a safety net
		if (this.tiles.size > cMaxCachedTiles) {
			const candidates = Array.from(this.tiles.values())
				.filter(tile => !displaySet.has(tile.id.key))
				.sort((a, b) => a.lastUsed - b.lastUsed)
			for (const tile of candidates.slice(0, this.tiles.size - cMaxCachedTiles)) {
				this.destroyTile(tile)
				this.tiles.delete(tile.id.key)
			}
		}
	}

	private destroyTile(tile: LoadedTile): void {
		// Renderer.destroy also destroys its geometry
		tile.buckets.forEach(bucket => bucket.renderer.destroy())
	}

	override destroy(): void {
		this.destroyed = true
		this.tiles.forEach(tile => this.destroyTile(tile))
		this.tiles.clear()
		this.displayKeys = []
		labelOccupancy.clear(`vector-labels:${this.options.source}`)
		if (this.labelOverlay) {
			const ebcontext = this.context as EarthBrowserContext
			ebcontext.selectionCoordinator.removeLabelNode(this.labelOverlay)
			this.labelOverlay.destroy()
			this.labelOverlay = undefined
		}
		super.destroy()
	}
}
